import { defineStore } from "pinia";
import type { Component } from "vue";
import { computed, markRaw, ref, watch } from "vue";
import { useRoute } from "vue-router";

import type * as Modals from "@/components/modal/modals";
import { getRouter } from "@/router";
import { setModalOpen } from "@/utils/shortcut/shortcuts";
import type { UnknownRecord } from "@/utils/type";
import {
  deserializeObject,
  firstQueryParam,
  removePropsWithPrefix,
  serializeObject,
} from "@/utils/vue/queryParam";

interface Modal {
  component: Component;
  name: Modals.ModalName;
  attrs: UnknownRecord;
  openedAt: number;
}

const QUERY_PREFIX = "modal";

export const useModalStore = defineStore("modal", () => {
  const modal = ref<Modal>();

  function isModalOpen(name?: Modals.ModalName) {
    return !!modal.value && (!name || modal.value.name === name);
  }

  const wasModalJustOpened = computed(
    () => !!modal.value && Date.now() - modal.value.openedAt < 100,
  );

  const route = useRoute();

  function open(name: Modals.ModalName, options?: { attrs?: UnknownRecord }) {
    updateQuery(name, options?.attrs || {});
  }

  function changeAttrs(attrs: UnknownRecord) {
    if (modal.value) {
      updateQuery(modal.value.name, { ...modal.value.attrs, ...attrs });
    }
  }

  function updateQuery(name: Modals.ModalName, props: UnknownRecord) {
    void getRouter().syncReplace(() => ({
      query: { ...route.query, modal: name, ...serializeObject(props, QUERY_PREFIX) },
    }));
  }

  function close() {
    void getRouter().syncReplace(() => ({
      query: removePropsWithPrefix(route.query, QUERY_PREFIX),
    }));
  }

  let modals: typeof Modals | undefined;

  watch(route, async (route) => {
    const queryModal = firstQueryParam(route.query, "modal");
    if (!queryModal && modal.value) {
      modal.value = undefined;
      setModalOpen(false);
    } else if (queryModal) {
      if (!modals) {
        modals = await import("@/components/modal/modals");
      }
      const component = modals.findModal(queryModal);
      if (component) {
        modal.value = {
          component: markRaw(component),
          name: queryModal as Modals.ModalName,
          attrs: deserializeObject(route.query, QUERY_PREFIX),
          openedAt: Date.now(),
        };
        setModalOpen(true);
      }
    }
  });

  return { modal, isModalOpen, wasModalJustOpened, open, close, changeAttrs };
});
